import { isClerkAPIResponseError } from "@clerk/nextjs/errors"
import type { ClerkAPIResponseError } from "@clerk/shared/types"

import { isClientNetworkError } from "@/lib/networkError"

const EXPECTED_CLERK_USER_ACTION_ERROR_CODES = new Set([
  "form_identifier_not_found",
  "form_identifier_exists",
  "form_code_incorrect",
  "form_param_format_invalid",
  "form_password_incorrect",
  "form_password_length_too_short",
  "form_password_or_identifier_incorrect",
  "form_password_validation_failed",
  "form_password_pwned",
  "verification_code_expired",
])

const CLERK_ENTERPRISE_SSO_PASSWORD_STRATEGY_MESSAGE =
  "`password` isn't allowed for `strategy` when enterprise SSO is used."
const CLERK_INVALID_VERIFICATION_STRATEGY_MESSAGE =
  "The verification strategy is not valid for this account"
const CLERK_ALREADY_SIGNED_IN_MESSAGE = "You're already signed in."
const CLERK_SESSION_EXISTS_CODE = "session_exists"

// Clerk does not always provide stable codes for expected auth-flow states;
// keep these message checks narrow and prefer codes above when available.
const EXPECTED_CLERK_USER_ACTION_ERROR_MESSAGES = [
  CLERK_ALREADY_SIGNED_IN_MESSAGE,
  CLERK_ENTERPRISE_SSO_PASSWORD_STRATEGY_MESSAGE,
  CLERK_INVALID_VERIFICATION_STRATEGY_MESSAGE,
]

const ENTERPRISE_SSO_SIGN_IN_MESSAGE =
  "This account uses enterprise SSO. Continue with your organization sign-in."

const IDENTIFIER_NOT_FOUND_CODE = "form_identifier_not_found"
const IDENTIFIER_NOT_FOUND_MESSAGE =
  "We couldn't sign you in with that email. Check the spelling, or use a different sign-in method."

function isClerkResponseError(error: unknown): error is ClerkAPIResponseError {
  return error != null && isClerkAPIResponseError(error)
}

function isExpectedClerkUserActionMessage(
  message: string | undefined,
): boolean {
  return EXPECTED_CLERK_USER_ACTION_ERROR_MESSAGES.some((expectedMessage) =>
    message?.includes(expectedMessage),
  )
}

function isEnterpriseSsoPasswordStrategyMessage(
  message: string | undefined,
): boolean {
  return (
    message?.includes(CLERK_ENTERPRISE_SSO_PASSWORD_STRATEGY_MESSAGE) ?? false
  )
}

function getClerkErrorMessage(
  error: ClerkAPIResponseError,
): string | undefined {
  return error.errors[0]?.longMessage ?? error.errors[0]?.message
}

export function extractClerkError(error: unknown, fallback: string): string {
  if (isClerkResponseError(error)) {
    const code = error.errors[0]?.code
    if (code === IDENTIFIER_NOT_FOUND_CODE) {
      return IDENTIFIER_NOT_FOUND_MESSAGE
    }
    const message = getClerkErrorMessage(error)
    if (isEnterpriseSsoPasswordStrategyMessage(message)) {
      return ENTERPRISE_SSO_SIGN_IN_MESSAGE
    }
    return message ?? fallback
  }
  if (error instanceof Error) {
    return error.message
  }
  return fallback
}

// Clerk rejects `signIn.create`, `signUp.create`, and `authenticateWithRedirect`
// with a `session_exists` error when a session is already active — surfaced to
// the user as "You're already signed in." Rather than show that as an error on
// the auth form, callers treat it as a signal to redirect an already-logged-in
// user onward.
export function isClerkSessionExistsError(error: unknown): boolean {
  if (!isClerkResponseError(error)) {
    return false
  }
  return error.errors.some(
    ({ code, longMessage, message }) =>
      code === CLERK_SESSION_EXISTS_CODE ||
      longMessage?.includes(CLERK_ALREADY_SIGNED_IN_MESSAGE) ||
      message?.includes(CLERK_ALREADY_SIGNED_IN_MESSAGE),
  )
}

export function shouldReportClerkAuthError(error: unknown): boolean {
  // A failed fetch to Clerk (offline, blocked, CORS) is the user's connectivity,
  // not a Coval auth fault — don't report it at error.
  if (isClientNetworkError(error)) {
    return false
  }

  if (!isClerkResponseError(error)) {
    return true
  }

  if (error.errors.length === 0) {
    return true
  }

  return error.errors.some(({ code, longMessage, message }) => {
    if (code && EXPECTED_CLERK_USER_ACTION_ERROR_CODES.has(code)) {
      return false
    }
    return !(
      isExpectedClerkUserActionMessage(longMessage) ||
      isExpectedClerkUserActionMessage(message)
    )
  })
}

export function shouldReportSignInError(error: unknown): boolean {
  return shouldReportClerkAuthError(error)
}
